import {
  type ComponentType,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import './info-strip.css'
import { PageActionFor } from './PageAction'

type IconType = ComponentType<{ size?: number; 'aria-hidden'?: boolean }>

/**
 * How the panel is reached where the strip is not shown.
 *
 * Required of a page strip, and refused to an inset one, because the two answer
 * to different chrome: a page strip gives way and the shell's row carries the
 * way in, while a strip inside a panel is already inside the thing a page
 * action would open. Stated in the type so a screen cannot ship without one —
 * a band that gives way with nothing standing in for it takes every setting it
 * held out of reach, and nothing about the screen looks wrong.
 */
type Opens = { label: string; onOpen: () => void }

/**
 * A band of facts about the record on screen, each of which is also the way to
 * change it.
 *
 * It replaces a toolbar of buttons. A toolbar asks you to recognize a control
 * and guess what it holds; a strip states the settings as sentences you can read
 * at a glance, and clicking one opens the panel at the part you just read. The
 * facts are what you came to check — the editing is what you occasionally do
 * about them — so the facts get the space and the chrome disappears.
 *
 * There are no labels: each cell leads with an icon that says what KIND of fact
 * it is, and the rest of the cell is the fact itself. A label above every value
 * would double the strip's height to name things the value already names.
 */

export function InfoStrip(
  props: {
    children: ReactNode
    /** Trailing edge — the overflow menu. Never a destructive button in the open:
     *  this is a band you read, not one you aim at. */
    actions?: ReactNode
  } & (
    | { variant?: 'page'; opens: Opens }
    /** `inset` drops the sticky positioning and the page's own gutter, for a strip
     *  embedded in a panel rather than spanning the screen. */
    | { variant: 'inset'; opens?: never }
  ),
) {
  const { children, actions, variant = 'page' } = props
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(true)

  /*
   * Whether the strip is showing is READ from the strip, not predicted.
   *
   * It gives way on its own container's measure — the column it was handed,
   * which narrows when a companion opens beside it and has no fixed relation to
   * the window. A viewport test would answer a different question and be wrong
   * in the band between: the strip gone, and the control that stands in for it
   * not yet offered. Observing the rendered box makes hiding it and offering
   * the way in ONE fact, which is the same reason both of its views read from
   * one summary.
   */
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new ResizeObserver(([entry]) => {
      const box = entry?.contentRect
      setShown(!!box && box.width > 0)
    })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  /*
   * Where the column is too narrow for it, the strip gives way entirely.
   *
   * The priority order settles that: the artifact and the controls that change
   * what it IS come first, and a band of settings about it comes after — four
   * stacked facts take a third of a phone screen above the message the screen
   * exists for.
   *
   * It draws no control of its own for the rest. A region gets ONE entry point,
   * and the strip sits under the top bar's own overflow and beside its actions
   * menu, so a third almost-identical affordance within a hundred pixels leaves
   * none of the three saying which is which. What it does instead is DECLARE
   * the way in, which the shell draws in the one row it owns — so the panel is
   * a tap away holding every fact in full, and the number of controls standing
   * in this region does not move.
   */
  return (
    <div
      ref={ref}
      className={`infostrip infostrip--${variant}`}
      /* Names the control that stands in for this band while it is not shown.
         The cells are shortcuts into a panel rather than controls of their own,
         so nothing else in the document connects them to the one entry point
         that reaches the same panel — and a band that has given way is
         indistinguishable from one whose settings are simply gone. */
      data-opens={!shown && props.opens ? props.opens.label : undefined}
    >
      {/* The way in, where the strip itself is not offering one. Declared
          rather than drawn: the shell owns the one row a page's entry points
          sit in, so the strip adds no control of its own and the region keeps
          a single way in. */}
      {!shown && props.opens ? (
        <PageActionFor
          type="settings"
          label={props.opens.label}
          onSelect={props.opens.onOpen}
        />
      ) : null}
      <div className="infostrip__cells">{children}</div>
      {actions ? <div className="infostrip__end">{actions}</div> : null}
    </div>
  )
}

/**
 * One fact in the strip. `value` carries the fact; `note` is the quiet
 * afterthought that rides with it (a count, a qualifier) and may be absent.
 */
export function InfoCell({
  icon: Icon,
  value,
  note,
  onClick,
  ariaLabel,
}: {
  icon: IconType
  value: ReactNode
  note?: ReactNode
  onClick?: () => void
  ariaLabel: string
}) {
  const inner = (
    <>
      <span className="infostrip__icon">
        <Icon size={15} aria-hidden />
      </span>
      <span className="infostrip__text">
        <span className="infostrip__value">{value}</span>
        {note ? <span className="infostrip__note">{note}</span> : null}
      </span>
    </>
  )

  // A cell with nothing to open is plain content, not a labeled widget — the
  // value reads as itself, so an aria-label would only rename it for a screen
  // reader and say it twice.
  if (!onClick) {
    return <div className="infostrip__cell">{inner}</div>
  }

  return (
    <button
      type="button"
      className="infostrip__cell infostrip__cell--action"
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {inner}
    </button>
  )
}
